import { useEffect, useState } from 'react';
import type { LeadDto } from '@mys/contracts';
import { replyState, REPLY_SLA_MINUTES } from '@mys/contracts/constants';
import { formatDuration } from '../lib/format.js';

/* ============================================================================
   The two-hour promise, on the screen.
   ----------------------------------------------------------------------------
   `/` promises a reply inside two hours. This is the only place in the
   dashboard that says how long is left, and it reads `replyState()` from
   `@mys/contracts/constants` rather than doing the arithmetic — the breach
   sweep and (in 1.7) the share-replied report derive from the same function,
   so "late" has one definition instead of three that agree until they do not.

   From `/constants`, not from the package index: one runtime import off the
   index drags Zod into this bundle. The type above is type-only and free.

   **Colour carries no information on its own here.** Overdue and a reply that
   came too late are both `pill--warn`, and the words tell them apart —
   "2h 23m late" against "replied in 3h 10m". Both are breaches of the same
   promise, so both read as one, and nobody has to remember which red is which.
   Neither variant introduces a colour combination: `pill`, `pill--warn` and
   `pill--muted` all already exist, and the contrast audit's PAIRS array is not
   auto-discovered.
   ========================================================================== */

/**
 * Now, re-read on a timer, so a countdown actually counts down.
 *
 * Thirty seconds: the display is in whole minutes, so a shorter tick re-renders
 * the board for no visible change, and a longer one can show a card as having
 * time left for most of a minute after it has not. **It is not a fetch** —
 * there is no `refetchInterval` anywhere in this dashboard, and adding one
 * would quietly undo the sliding idle session, which depends on an open tab
 * sending nothing. This re-renders from local state and sends no request.
 */
function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return now;
}

export type ReplyLead = Pick<LeadDto, 'createdAt' | 'firstRepliedAt' | 'status'>;

/** The words and the pill class for one lead. Pure, and exported so a test can
    read the label without rendering a tree or waiting for a timer. */
export function replyLabel(
  lead: ReplyLead,
  now: Date,
): { text: string; className: string; title: string } {
  const state = replyState(lead, now);

  switch (state.kind) {
    case 'waiting':
      return {
        text: `${formatDuration(state.minutes)} left`,
        className: 'pill',
        title: `Reply promised inside ${formatDuration(REPLY_SLA_MINUTES)} of the enquiry`,
      };
    case 'overdue':
      return {
        text: `${formatDuration(state.minutes)} late`,
        className: 'pill pill--warn',
        title: `Past the ${formatDuration(REPLY_SLA_MINUTES)} promise with nothing recorded`,
      };
    case 'replied':
      return {
        text: `replied in ${formatDuration(state.minutes)}`,
        className: state.late ? 'pill pill--warn' : 'pill pill--muted',
        title: state.late
          ? `Answered, but after the ${formatDuration(REPLY_SLA_MINUTES)} promise`
          : 'Answered inside the promise',
      };
    case 'closed':
      /* Won or lost with nothing recorded. NOT called a breach: this field
         only ever sees what passed through the dashboard, and the owner
         answers most enquiries on WhatsApp from their own phone. Saying "no
         reply recorded" is the true statement; saying "late" would not be. */
      return {
        text: 'no reply recorded',
        className: 'pill pill--muted',
        title: 'Closed without a reply being recorded here',
      };
  }
}

export function ReplyClock({ lead }: { lead: ReplyLead }) {
  const now = useNow();
  const { text, className, title } = replyLabel(lead, now);

  return (
    <span className={className} title={title}>
      {text}
    </span>
  );
}
